/**
 * useWidgetVisibility — Widget açık/kapalı durumu yönetimi
 * localStorage'da persist + cross-component sync (custom event)
 */
import { useEffect, useState } from 'react'

export type WidgetId =
  | 'clock' | 'tasks' | 'flz' | 'skills' | 'quickaccess'
  | 'neural' | 'pulse' | 'twin' | 'flzhub' | 'flzfamily'
  | 'research' | 'work' | 'social' | 'workspace' | 'metrics'

const STORAGE_KEY = 'runai-widgets-hidden'
const EVENT = 'runai:widgets-changed'

function readHidden(): Set<WidgetId> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return new Set(raw ? JSON.parse(raw) : [])
  } catch {
    return new Set()
  }
}

function writeHidden(hidden: Set<WidgetId>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...hidden]))
  window.dispatchEvent(new CustomEvent(EVENT))
}

export function useWidgetVisibility() {
  const [hidden, setHidden] = useState<Set<WidgetId>>(readHidden)

  useEffect(() => {
    const onChange = () => setHidden(readHidden())
    window.addEventListener(EVENT, onChange)
    return () => window.removeEventListener(EVENT, onChange)
  }, [])

  const isVisible = (id: WidgetId) => !hidden.has(id)

  const hide = (id: WidgetId) => {
    const next = new Set(hidden)
    next.add(id)
    writeHidden(next)
  }

  const show = (id: WidgetId) => {
    const next = new Set(hidden)
    next.delete(id)
    writeHidden(next)
  }

  const showAll = () => {
    writeHidden(new Set())
  }

  const hideAll = () => {
    const all: WidgetId[] = ['clock', 'tasks', 'flz', 'skills', 'quickaccess',
      'neural', 'pulse', 'twin', 'flzhub', 'flzfamily', 'research', 'work', 'social', 'workspace']
    writeHidden(new Set(all))
  }

  return { hidden, isVisible, hide, show, showAll, hideAll }
}
